import { HUB_VCARD_CATEGORIES } from "../constants/vcardCategories";
import type {
  SmartTagAttachmentKey,
  SmartTagAttachmentVisibility,
  VCardCategory,
} from "../types/vcard";

export interface SmartTagAttachmentConfig {
  label: string;
  description: string;
  allowedMimeTypes: readonly string[];
  maxSizeBytes: number;
  // Hard ceiling per card, per attachment key. A reasonable bound for the
  // realistic content (e.g., warranty docs per device, itinerary docs per
  // luggage tag) and a defence against runaway sync loops or accidents.
  maxFilesPerCard: number;
  defaultVisibility: SmartTagAttachmentVisibility;
  supportedCategories: readonly VCardCategory[];
}

const MEGABYTE = 1024 * 1024;

const DOCUMENT_AND_IMAGE_MIME_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
] as const;

export const SMART_TAG_ATTACHMENT_CONFIG: Record<
  SmartTagAttachmentKey,
  SmartTagAttachmentConfig
> = {
  warranty: {
    label: "Warranty & Proof of Purchase",
    description:
      "Receipts, warranty cards, and proof-of-purchase documents for this device.",
    allowedMimeTypes: DOCUMENT_AND_IMAGE_MIME_TYPES,
    maxSizeBytes: 25 * MEGABYTE,
    maxFilesPerCard: 10,
    defaultVisibility: "owner-only",
    supportedCategories: [
      "myTech",
      "extremeKit",
      "snowGear_snowboards",
      "snowGear_helmetMask",
      "snowGear_boots",
      "snowGear_glovesApparel",
      "business",
      "home",
    ],
  },
  identityDocuments: {
    label: "Identity & Civil Documents",
    description:
      "Passports, birth certificates, marriage certificates, and citizenship documents.",
    allowedMimeTypes: DOCUMENT_AND_IMAGE_MIME_TYPES,
    maxSizeBytes: 25 * MEGABYTE,
    maxFilesPerCard: 10,
    defaultVisibility: "owner-only",
    supportedCategories: ["luggage"],
  },
  travelItinerary: {
    label: "Flights, Accommodation and Travel Documents",
    description:
      "Private flight, accommodation, travel, insurance, and policy documents for this luggage tag.",
    allowedMimeTypes: DOCUMENT_AND_IMAGE_MIME_TYPES,
    maxSizeBytes: 25 * MEGABYTE,
    maxFilesPerCard: 10,
    defaultVisibility: "owner-only",
    supportedCategories: ["luggage"],
  },
  healthRecords: {
    label: "Health Records, X-Rays etc",
    description:
      "Private health records, X-rays, and supporting emergency documents.",
    allowedMimeTypes: DOCUMENT_AND_IMAGE_MIME_TYPES,
    maxSizeBytes: 25 * MEGABYTE,
    maxFilesPerCard: 10,
    defaultVisibility: "owner-only",
    supportedCategories: ["extremeKit"],
  },
};

// Module-load validation: hub categories must never appear in any
// supportedCategories list. Throws at import time so misconfiguration is
// caught during build/dev startup, not at user runtime.
const validateRegistry = (): void => {
  for (const [key, config] of Object.entries(SMART_TAG_ATTACHMENT_CONFIG)) {
    for (const category of config.supportedCategories) {
      if (HUB_VCARD_CATEGORIES.has(category)) {
        throw new Error(
          `SMART_TAG_ATTACHMENT_CONFIG["${key}"].supportedCategories contains hub category "${category}". ` +
            `Hub categories must not receive attachment UI. Use leaf categories only.`,
        );
      }
    }
  }
};

validateRegistry();

export const getAttachmentKeysForCategory = (
  category: VCardCategory,
): SmartTagAttachmentKey[] => {
  const keys: SmartTagAttachmentKey[] = [];
  for (const [key, config] of Object.entries(SMART_TAG_ATTACHMENT_CONFIG)) {
    if (config.supportedCategories.includes(category)) {
      keys.push(key as SmartTagAttachmentKey);
    }
  }
  return keys;
};

export const getAttachmentConfig = (
  key: SmartTagAttachmentKey,
): SmartTagAttachmentConfig => SMART_TAG_ATTACHMENT_CONFIG[key];
